import React from 'react';
import { Box, Skeleton } from '@ublend-npm/aulaui-core';
import { createStyles } from './AssignmentSkeletons.styles';

export const AssignmentListSkeleton = ({ count = 5 }: { count?: number }) => {
  const styles = createStyles();

  return (
    <Box aria-busy="true" aria-label="Loading assignments" role="status">
      <Box aria-hidden="true" sx={styles.listItems}>
        {Array.from({ length: count }, (_, index) => (
          <Box key={index} sx={styles.assignmentCard}>
            <Box sx={styles.assignmentCardHeader}>
              <Skeleton kind="circular" width={24} height={24} />
              <Skeleton kind="rounded" width="58%" height={20} />
              <Skeleton
                kind="rounded"
                width={56}
                height={20}
                sx={styles.assignmentCardStatus}
              />
            </Box>
            <Skeleton kind="text" width="82%" height={18} />
            <Skeleton kind="text" width="42%" height={16} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const SkeletonHeader = ({ submissions }: { submissions: boolean }) => {
  const styles = createStyles();

  return (
    <Box sx={styles.headerCard}>
      <Box sx={styles.headerTitle}>
        <Skeleton kind="text" width={submissions ? '46%' : '72%'} height={32} />
        {!submissions && <Skeleton kind="text" width="88%" height={20} />}
      </Box>
      {!submissions && (
        <Skeleton
          kind="rounded"
          width={176}
          height={32}
          sx={styles.linkedMaterial}
        />
      )}
      <Box sx={styles.metadataGrid}>
        {Array.from({ length: 4 }, (_, index) => (
          <Box key={index} sx={styles.metadataItem}>
            <Skeleton kind="circular" width={20} height={20} />
            <Box sx={styles.metadataText}>
              <Skeleton kind="text" width="72%" height={16} />
              <Skeleton kind="text" width="92%" height={16} />
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={styles.headerActions}>
        <Skeleton kind="rounded" width={submissions ? 136 : 160} height={36} />
        <Skeleton kind="rounded" width={116} height={36} />
      </Box>
    </Box>
  );
};

const SubmissionAccordionSkeleton = () => {
  const styles = createStyles();

  return (
    <Box sx={styles.accordion}>
      <Skeleton kind="text" width="38%" height={18} />
      <Skeleton kind="text" width={28} height={18} />
    </Box>
  );
};

/** A content-area placeholder; it intentionally does not cover the sidebar. */
export const AssignmentDetailSkeleton = ({
  submissions = false,
  showSubmissions = false,
}: {
  submissions?: boolean;
  showSubmissions?: boolean;
}) => {
  const styles = createStyles();

  return (
    <Box
      aria-busy="true"
      aria-label={
        submissions ? 'Loading submissions' : 'Loading assignment details'
      }
      role="status"
      sx={styles.detailContainer}
    >
      <Box aria-hidden="true" sx={styles.detailContent}>
        <SkeletonHeader submissions={submissions} />
        {(submissions || showSubmissions) && <SubmissionSectionSkeleton />}
      </Box>
    </Box>
  );
};

/** Matches the submission search and filter controls. */
export const SubmissionSectionSkeleton = () => {
  const styles = createStyles();

  return (
    <Box aria-busy="true" aria-label="Loading submissions" role="status">
      <Box aria-hidden="true" sx={styles.submissionContent}>
        <Skeleton kind="text" width="38%" height={28} />
        <Box sx={styles.submissionControls}>
          <Skeleton
            kind="rounded"
            width={220}
            height={32}
            sx={styles.filterControl}
          />
          <Skeleton
            kind="rounded"
            width="100%"
            height={32}
            sx={styles.searchControl}
          />
        </Box>
        <Box sx={styles.accordionList}>
          <SubmissionAccordionSkeleton />
          <SubmissionAccordionSkeleton />
          <SubmissionAccordionSkeleton />
        </Box>
      </Box>
    </Box>
  );
};
